import { Card, CardBody, PageHeader } from "@fellowflats/ui";
import { AnalyticsSchema } from "@fellowflats/contracts/admin";
import { api } from "../lib/api";
import { useApi } from "../lib/use-api";
import { Loaded } from "../components/bits";

const pct = (n: number | null) => (n == null ? "n/a" : `${Math.round(n * 100)}%`);

/** Fourteen bars, one per day, scaled to the busiest day. */
function Bars({ title, series }: { title: string; series: { day: string; count: number }[] }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <Card>
      <CardBody className="p-5">
        <h2 className="font-semibold">{title}</h2>
        <div className="mt-4 flex h-32 items-end gap-1" role="img" aria-label={`${title}: ${series.map((s) => `${s.day} ${s.count}`).join(", ")}`}>
          {series.map((s) => (
            <div key={s.day} className="flex h-full flex-1 flex-col items-center justify-end gap-1" title={`${s.day}: ${s.count}`}>
              <div className="w-full rounded-t bg-primary/80" style={{ height: `${(s.count / max) * 100}%`, minHeight: s.count ? 4 : 1 }} />
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Last 14 days, {series.reduce((a, s) => a + s.count, 0)} in total</p>
      </CardBody>
    </Card>
  );
}

export function Analytics() {
  const state = useApi(() => api("GET", "/admin/analytics", AnalyticsSchema));
  return (
    <div className="space-y-6">
      <PageHeader title="Analytics" description="Marketplace health from FellowFlats data." />
      <Loaded state={state}>
        {(a) => {
          const stats = [
            ["Total users", a.users],
            ["Active listings", a.activeListings],
            ["Renters looking", a.lookingTenants],
            ["Booking requests", a.totalBookings],
            ["Requests per active listing", a.activeListings ? (a.totalBookings / a.activeListings).toFixed(1) : "0"],
            ["Approval rate", pct(a.approvalRate)],
            ["Average time to approval", a.avgHoursToApproval == null ? "n/a" : `${a.avgHoursToApproval.toFixed(1)} h`],
            ["Fill rate (occupied beds)", pct(a.fillRate)],
          ] as const;
          return (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {stats.map(([label, value]) => (
                  <Card key={label}>
                    <CardBody className="p-5">
                      <p className="text-2xl font-bold tracking-tight">{value}</p>
                      <p className="text-sm text-muted-foreground">{label}</p>
                    </CardBody>
                  </Card>
                ))}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Bars title="Sign-ups" series={a.signups14d} />
                <Bars title="Booking requests" series={a.requests14d} />
              </div>
            </>
          );
        }}
      </Loaded>
    </div>
  );
}
